import { useState, useEffect, useCallback } from "react";
import { KeyRound, Plus, Trash2, Clock, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { generatePasscode, listPasscodes, revokePasscode } from "../utils/api";

const MAX_CODES   = 5;
const MAX_USES    = 4;
const TTL_MINUTES = 30;

function fmtExpiry(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  if (diffMs <= 0) return "Expired";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "< 1 min";
  return `${mins} min${mins !== 1 ? "s" : ""}`;
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true
  });
}

function UsageDots({ used, max }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {Array.from({ length: max }).map((_, i) => (
        <div
          key={i}
          title={i < used ? "Used" : "Available"}
          style={{
            width: 10, height: 10, borderRadius: "50%",
            background: i < used ? "#1976e0" : "#dfe3e8",
            transition: "background 0.2s"
          }}
        />
      ))}
    </div>
  );
}

export default function Passcode() {
  const [codes, setCodes]         = useState([]);
  const [activeCount, setCount]   = useState(0);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [revoking, setRevoking]   = useState(null);
  const [newCode, setNewCode]     = useState(null);   // highlight freshly generated code

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listPasscodes();
      setCodes(res.data.codes);
      setCount(res.data.active_count);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load passcodes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh expiry countdown every 30 s
  useEffect(() => {
    const id = setInterval(() => setCodes(c => [...c]), 30000);
    return () => clearInterval(id);
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setSuccess("");
    setNewCode(null);
    try {
      const res = await generatePasscode();
      setNewCode(res.data.code._id);
      setSuccess(`Code "${res.data.code.code}" generated — valid for ${TTL_MINUTES} min, max ${MAX_USES} uses.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to generate passcode");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id) => {
    setRevoking(id);
    setError("");
    setSuccess("");
    try {
      await revokePasscode(id);
      setSuccess("Passcode revoked.");
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to revoke passcode");
    } finally {
      setRevoking(null);
    }
  };

  const canGenerate = activeCount < MAX_CODES;

  return (
    <div>
      <h1 className="page-title">App Passcodes</h1>
      <p className="page-sub">
        Control early-access to app.thehealthhire.com. Each code expires in {TTL_MINUTES} min
        and can be used up to {MAX_USES} times.
      </p>

      {error   && <div className="error-box"   style={{ marginTop: 16 }}>{error}</div>}
      {success && <div className="success-box" style={{ marginTop: 16 }}>{success}</div>}

      {/* Stats + Generate */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, margin: "20px 0 16px"
      }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div className="card" style={{
            padding: "12px 18px", display: "flex", alignItems: "center", gap: 12
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: "#e8f1fd",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <KeyRound size={18} style={{ color: "#1976e0" }} />
            </div>
            <div>
              <p style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{activeCount}</p>
              <p style={{ fontSize: 11, color: "#718096", fontWeight: 600, marginTop: 2 }}>
                Active / {MAX_CODES} max
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading} title="Refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={generating || !canGenerate}
            title={!canGenerate ? `Maximum of ${MAX_CODES} codes active` : "Generate new code"}
          >
            {generating
              ? <><RefreshCw size={14} className="animate-spin" /> Generating…</>
              : <><Plus size={14} /> Generate Code</>}
          </button>
        </div>
      </div>

      {!canGenerate && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
          padding: "10px 14px", fontSize: 13, color: "#92400e", marginBottom: 14,
          display: "flex", alignItems: "center", gap: 8
        }}>
          <AlertCircle size={15} />
          Maximum of {MAX_CODES} active codes reached. Revoke one to generate a new code.
        </div>
      )}

      {/* Code list */}
      <div className="card table-wrap">
        {loading ? (
          <div className="empty">
            <span className="spinner spinner-dark" style={{ width: 24, height: 24 }} />
          </div>
        ) : codes.length === 0 ? (
          <div className="empty" style={{ padding: "40px 0", textAlign: "center" }}>
            <KeyRound size={32} style={{ color: "#dfe3e8", margin: "0 auto 12px" }} />
            <p style={{ fontWeight: 600, color: "#718096" }}>No active passcodes</p>
            <p style={{ fontSize: 13, color: "#a0aec0", marginTop: 4 }}>
              Generate a code to let users into the app.
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Expires</th>
                <th>Created by</th>
                <th>Created at</th>
                <th>Uses</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const isNew    = c._id === newCode;
                const expiring = fmtExpiry(c.expires_at) !== "Expired" &&
                                 (new Date(c.expires_at) - new Date()) < 5 * 60 * 1000;

                return (
                  <tr key={c._id} style={isNew ? { background: "#f0fdf4" } : undefined}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          fontFamily: "monospace", fontSize: 17, fontWeight: 800,
                          letterSpacing: "0.18em", color: "#1a1f2e",
                          background: "#f4f6f8", padding: "4px 10px", borderRadius: 8
                        }}>
                          {c.code}
                        </span>
                        {isNew && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, background: "#f0fdf4",
                            color: "#16a34a", border: "1px solid #bbf7d0",
                            borderRadius: 999, padding: "2px 7px"
                          }}>
                            NEW
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Clock size={13} style={{ color: expiring ? "#d97706" : "#a0aec0" }} />
                        <span style={{
                          fontSize: 13, fontWeight: 600,
                          color: expiring ? "#d97706" : "#718096"
                        }}>
                          {fmtExpiry(c.expires_at)}
                        </span>
                      </div>
                    </td>
                    <td style={{ color: "#718096", fontSize: 13 }}>{c.created_by_name || "—"}</td>
                    <td style={{ color: "#718096", fontSize: 13, whiteSpace: "nowrap" }}>
                      {fmtTime(c.created_at)}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <UsageDots used={c.use_count} max={c.max_uses} />
                        <span style={{ fontSize: 12, color: "#a0aec0" }}>
                          {c.use_count}/{c.max_uses}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRevoke(c._id)}
                        disabled={revoking === c._id}
                        title="Revoke"
                      >
                        {revoking === c._id
                          ? <RefreshCw size={12} className="animate-spin" />
                          : <Trash2 size={12} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* How it works */}
      <div className="card" style={{ marginTop: 24, padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "#1a1f2e" }}>
          How it works
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            ["Code validity", `Each code is valid for ${TTL_MINUTES} minutes from generation`],
            ["Max uses", `Each code can be entered at most ${MAX_USES} times`],
            ["Access duration", "Once entered, a user gets 2.5 hours of access before needing a new code"],
            ["Limit", `Maximum ${MAX_CODES} active codes can exist at any time`],
            ["Revoke", "Revoking a code immediately invalidates it — active sessions are unaffected until they expire"],
          ].map(([title, desc]) => (
            <div key={title} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <CheckCircle size={14} style={{ color: "#1976e0", marginTop: 2, flexShrink: 0 }} />
              <p style={{ fontSize: 13, color: "#718096", lineHeight: 1.5 }}>
                <strong style={{ color: "#1a1f2e" }}>{title}:</strong> {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
